import React, { useCallback, useRef } from 'react'
import { BlockAnchors } from '@constants/forms'
import { PledgeSummaryResponse } from '@http/api-models/PledgeSummaryResponse'
import { apiUrls } from '@http/api-urls'
import { Map, YMaps } from '@pbe/react-yandex-maps'
import { Section } from '@shared/components/section'
import { getConfigProperty, isProduction } from '@shared/pipes/config'
import { breaksHundreds } from '@shared/utils/helpers'
import { wordEnding } from '@shared/utils/wordEnding'
import styled from 'styled-components'
import { useTheme } from 'styled-components'

interface SectionMapProps {
	pledgeSummary?: PledgeSummaryResponse | undefined
}

export const SectionMap = ({ pledgeSummary }: SectionMapProps): JSX.Element | null => {
	const mapInstanceRef = useRef<any>()
	const theme = useTheme()

	const handleLoad = useCallback(
		(ymaps) => {
			if (!mapInstanceRef.current) {
				return
			}
			const CustomLayoutClass = ymaps.templateLayoutFactory.createClass(
				`<div style="font-family: 'Uni Neue',serif">
<h4 style="font-size:16px;line-height:22px;">Квартира</h4>
<p style="font-size:14px;line-height:18px;">{{properties.address}}<br/>
Площадь: {{properties.areaFormat}} м<sup>2</sup><br/>
Стоимость: {{properties.amountFormat}} ₽</p>
</div>`
			)
			const loadingObjectManager = new ymaps.LoadingObjectManager(
				getConfigProperty('API_URL') + apiUrls.getMapPledgeList(),
				{
					clusterize: true,
					clusterHasBalloon: false,
					geoObjectBalloonContentLayout: CustomLayoutClass,
				}
			)
			loadingObjectManager.objects.events.add('click', (e: any) => {
				const objectId = e.get('objectId')
				const obj = loadingObjectManager.objects.getById(objectId)
				obj.properties.areaFormat = `${obj.properties.area}`.replaceAll('.', ',')
				obj.properties.amountFormat = `${breaksHundreds(
					obj.properties.estimatedAmount
				)}`.replaceAll('.', ',')
			})
			loadingObjectManager.objects.options.set({
				preset: 'islands#circleIcon',
				iconColor: theme.colors.main,
			})
			loadingObjectManager.clusters.options.set({
				preset: 'islands#circleClusterIcons',
				iconColor: theme.colors.main,
			})
			mapInstanceRef.current.geoObjects.add(loadingObjectManager)
		},
		[theme.colors.main]
	)

	if (!pledgeSummary) {
		return null
	}

	return (
		<Section id={BlockAnchors.security}>
			<ViewRoot>
				<TextTitle>Недвижимость в залоге</TextTitle>
				<Text>
					Залог недвижимости помогает защитить средства инвесторов и обеспечить регулярные выплаты
					по Сбережениям. Если у заёмщиков возникают просрочки, недвижимость по решению суда
					реализовывается на торгах. При нехватке свободных средств на выплаты часть портфеля займов
					может быть продана через механизм залоговых закладных.
				</Text>
				<ViewGridInfo>
					<TextInfo>Всего объектов в залоге</TextInfo>
					<TextInfoValue>
						{pledgeSummary.count}{' '}
						{wordEnding(pledgeSummary.count!, { 1: 'квартира', 2: 'квартиры', 5: 'квартир' })}
					</TextInfoValue>
					<TextInfo>Общая рыночная стоимость</TextInfo>
					<TextInfoValue>{breaksHundreds(pledgeSummary.totalEstimatedAmount!)} ₽</TextInfoValue>
					<TextInfo>Общая площадь</TextInfo>
					<TextInfoValue>
						{breaksHundreds(pledgeSummary.totalArea!)}&nbsp;м<sup>2</sup>
					</TextInfoValue>
				</ViewGridInfo>
				<ViewMap>
					<YMaps
						query={{
							apikey: 'b4a0cc9e-bbaa-4581-8fbc-0984b9004d26',
							mode: isProduction ? 'release' : 'debug',
						}}
					>
						<Map
							defaultState={{
								center: [61, 69],
								zoom: 4,
								controls: ['zoomControl'],
							}}
							width='100%'
							height='100%'
							modules={[
								'control.ZoomControl',
								'LoadingObjectManager',
								'templateLayoutFactory',
								'objectManager.addon.objectsBalloon',
							]}
							onLoad={handleLoad}
							instanceRef={mapInstanceRef}
						/>
					</YMaps>
				</ViewMap>
			</ViewRoot>
		</Section>
	)
}

const ViewRoot = styled.div(({ theme }) => ({
	marginBottom: 60,
	paddingLeft: 20,
	paddingRight: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 100,
		paddingLeft: 60,
		paddingRight: 60,
	},
}))

const TextTitle = styled.h2(({ theme }) => ({
	...theme.typography.header30_36,
	marginBottom: 16,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header56_60,
		marginBottom: 24,
	},
}))

const ViewGridInfo = styled.div(({ theme }) => ({
	display: 'grid',
	justifyContent: 'space-between',
	gridTemplate: 'auto auto auto auto auto auto / auto',
	gridGap: '0 16px',
	[theme.mediaQueries.mediaMinWidthMobile]: {
		gridTemplate: 'auto auto / auto auto auto',
		gridAutoFlow: 'column',
	},
}))

const TextInfo = styled.p(({ theme }) => ({
	...theme.typography.text18_24,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
	},
}))

const Text = styled.p(({ theme }) => ({
	...theme.typography.text18_24,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 30,
		...theme.typography.text24_32,
	},
}))

const TextInfoValue = styled.p(({ theme }) => ({
	whiteSpace: 'nowrap',
	...theme.typography.header24_32,
	marginBottom: 16,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header36_48,
		marginBottom: 32,
	},
}))

const ViewMap = styled.div(({ theme }) => ({
	borderRadius: 40,
	overflow: 'hidden',
	height: 360,
	aspectRatio: 'auto',
	width: '100%',
	[theme.mediaQueries.mediaMinWidthMobile]: {
		aspectRatio: '1040/578',
		height: 'auto',
	},
}))
